import {
	HeadContent,
	Scripts,
	createRootRouteWithContext,
	retainSearchParams,
	stripSearchParams,
} from '@tanstack/react-router';
import appCss from '../app.css?url';
import { type QueryClient, isServer } from '@tanstack/react-query';
import { DefaultCatchBoundary } from './-components/default-catch-boundary';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import { FormDevtoolsPanel } from '@tanstack/react-form-devtools';
import { NotFound } from './-components/not-found';
import * as z from 'zod';
import { portalLocalesExtended } from '@worldcoo/monolith/portals';
import { Toaster } from '@worldcoo/ui/components/sonner';
import { getRequestContext } from '@/lib/server-functions';
import { I18nProvider } from '@lingui/react';
import { i18n } from '@lingui/core';

export interface MyRouterContext {
	queryClient: QueryClient;
}

const defaultValues = {
	q: '',
};

export const Route = createRootRouteWithContext<MyRouterContext>()({
	validateSearch: z.object({
		q: z.string().default(defaultValues.q).catch(''),
		lang: z.enum(portalLocalesExtended).or(z.undefined()).catch(undefined).optional(),
		iframe: z.literal(true).or(z.undefined()).catch(undefined).optional(),
		city: z.string().optional(),
	}),
	search: {
		// TODO: retainSearchParams is not working correctly with default values and stripSearchParams,
		// so we're passing search={(prev) => prev} manually in the links for now
		middlewares: [stripSearchParams(defaultValues), retainSearchParams(true)],
	},
	beforeLoad: ({ search }) => getRequestContext({ data: search.lang }),
	head: () => ({
		meta: [
			{
				charSet: 'utf-8',
			},
			{
				name: 'viewport',
				content: 'width=device-width, initial-scale=1',
			},
		],
		styles: [
			{
				children:
					'@font-face {' +
					"font-family: 'Rethink Sans Variable';" +
					'font-style: normal;' +
					'font-display: swap;' +
					'font-weight: 300 800;' +
					`src: url('${import.meta.env.VITE_CDN_URL}/rethink-sans-variable.ttf');` +
					'}',
			},
		],
		links: [
			{
				rel: 'preload',
				href: `${import.meta.env.VITE_CDN_URL}/rethink-sans-variable.ttf`,
				as: 'font',
				type: 'font/ttf',
				crossOrigin: 'anonymous',
			},
			{
				rel: 'stylesheet',
				// There is a weird interaction with renderBuiltUrl where the appCss is not getting
				// rewritten properly when server side rendering. This doesn't affect client side.
				href: isServer ? `${import.meta.env.VITE_CDN_URL}${appCss}` : appCss,
			},
		],
		scripts: [
			// Recaptcha
			{
				src: `https://www.google.com/recaptcha/api.js?render=${import.meta.env.VITE_GOOGLE_RECAPTCHA_SITE_KEY}`,
				defer: true,
			},
			// Google Analytics
			{
				src: `https://www.googletagmanager.com/gtag/js?id=${import.meta.env.VITE_ANALYTICS_CODE}`,
				defer: true,
			},
		],
	}),
	errorComponent: (props) => (
		<I18nProvider i18n={i18n}>
			<DefaultCatchBoundary {...props} />
		</I18nProvider>
	),
	notFoundComponent: () => <NotFound />,
	shellComponent: RootDocument,
});

function RootDocument({ children }: { children: React.ReactNode }) {
	const { locale } = Route.useRouteContext();

	return (
		<html lang={locale}>
			<head>
				<HeadContent />
			</head>
			<body className="min-h-dvh overflow-y-scroll">
				{children}
				<Toaster
					className="whitespace-pre-line print:hidden"
					offset={{ top: 'calc(32px + env(safe-area-inset-top))' }}
					mobileOffset={{ top: 'calc(16px + env(safe-area-inset-top))' }}
					position="top-center"
					theme="light"
				/>

				<div className="hidden sm:block">
					<TanStackDevtools
						plugins={[
							{
								name: 'Tanstack Router',
								render: <TanStackRouterDevtoolsPanel />,
							},
							{
								name: 'Tanstack Query',
								render: <ReactQueryDevtoolsPanel />,
							},
							{
								name: 'Tanstack Form',
								render: <FormDevtoolsPanel />,
							},
						]}
					/>
				</div>
				<Scripts />
			</body>
		</html>
	);
}
